"use client";
import { createAnalyticsClient } from "@repo/analytics";
import { isProductionTelemetryEnabled } from "../telemetry";
import { trackAmplitudeEvent, trackAmplitudePageView } from "./amplitude";

/**
 * Google Tag Manager (GTM) and Google Analytics 4 (GA4) Integration
 *
 * This module provides GTM data layer pushing and GA4 ecommerce event tracking.
 * App-level events are pushed from `product-analytics.ts` into the same dataLayer.
 *
 * Analytics tags require analytics consent; advertising permissions also require marketing consent.
 */

declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
  }
}

export function buildGtmConsentState(analyticsConsented: boolean, marketingConsented: boolean) {
  const advertisingConsent = marketingConsented ? "granted" : "denied";

  return {
    ad_storage: advertisingConsent,
    analytics_storage: analyticsConsented ? "granted" : "denied",
    ad_user_data: advertisingConsent,
    ad_personalization: advertisingConsent,
  } as const;
}

export function setGtmConsent(
  mode: "default" | "update",
  analyticsConsented: boolean,
  marketingConsented: boolean,
): void {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag || ((...args) => window.dataLayer.push(args as unknown as Record<string, unknown>));
  window.gtag("consent", mode, buildGtmConsentState(analyticsConsented, marketingConsented));
}

// GTM/GA4 Event name constants
export const GTMEvents = {
  // Page/Screen
  PAGE_VIEW: "page_view",

  // Ecommerce - Product Interactions
  VIEW_ITEM: "view_item",
  VIEW_ITEM_LIST: "view_item_list",
  SELECT_ITEM: "select_item",
  ADD_TO_CART: "add_to_cart",
  REMOVE_FROM_CART: "remove_from_cart",
  VIEW_CART: "view_cart",

  // Ecommerce - Checkout
  BEGIN_CHECKOUT: "begin_checkout",
  ADD_SHIPPING_INFO: "add_shipping_info",
  ADD_PAYMENT_INFO: "add_payment_info",
  PURCHASE: "purchase",

  // User Actions
  SIGN_UP: "sign_up",
  LOGIN: "login",
  SEARCH: "search",

  // Engagement
  GENERATE_LEAD: "generate_lead",
  CONTACT: "contact",
} as const;

// Type for event names
type GTMEventName = (typeof GTMEvents)[keyof typeof GTMEvents];

// GA4 Ecommerce Item interface
export interface GA4Item {
  item_id: string;
  item_name: string;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_variant?: string;
  price: number;
  quantity?: number;
  currency: string;
}

// GA4 Ecommerce Event Parameters
interface EcommerceParams {
  currency?: string;
  value?: number;
  items?: GA4Item[];
  transaction_id?: string;
  tax?: number;
  shipping?: number;
  coupon?: string;
}

// Base event parameters
interface BaseEventParams {
  event_category?: string;
  event_label?: string;
  value?: number;
  currency?: string;
  [key: string]: unknown;
}

const analyticsClient = createAnalyticsClient({
  app: "tutuly",
  env: process.env.NEXT_PUBLIC_APP_ENV || process.env.NODE_ENV || "development",
});

/**
 * Track a generic GTM/GA4 event
 */
export function trackGTMEvent(eventName: GTMEventName, params: BaseEventParams = {}): void {
  if (!isProductionTelemetryEnabled()) {
    return;
  }

  trackAmplitudeEvent(eventName, params);
  analyticsClient.track(eventName, params);
}

/**
 * Track GA4 ecommerce event with items
 */
export function trackEcommerceEvent(eventName: GTMEventName, params: EcommerceParams): void {
  if (!isProductionTelemetryEnabled()) {
    return;
  }

  trackAmplitudeEvent(eventName, {
    ecommerce: {
      ...params,
    },
  });
  analyticsClient.track(eventName, {
    ecommerce: {
      ...params,
    },
  });
}

/**
 * Track page view
 */
export function trackPageView(
  pagePath: string,
  pageTitle: string,
  additionalParams: Record<string, unknown> = {},
): void {
  if (!isProductionTelemetryEnabled()) {
    return;
  }

  const pageViewPayload = {
    page_path: pagePath,
    page_title: pageTitle,
    page_location: typeof window !== "undefined" ? window.location.href : pagePath,
    ...additionalParams,
  };
  trackAmplitudePageView(pagePath, pageTitle, additionalParams);
  analyticsClient.track(GTMEvents.PAGE_VIEW, pageViewPayload);
}

/**
 * Track product view
 */
export function trackProductView(
  item: GA4Item,
  additionalParams: Record<string, unknown> = {},
): void {
  trackEcommerceEvent(GTMEvents.VIEW_ITEM, {
    currency: item.currency,
    value: item.price,
    items: [item],
    ...additionalParams,
  });
}

/**
 * Track add to cart
 */
export function trackAddToCart(
  item: GA4Item,
  additionalParams: Record<string, unknown> = {},
): void {
  trackEcommerceEvent(GTMEvents.ADD_TO_CART, {
    currency: item.currency,
    value: item.price * (item.quantity || 1),
    items: [item],
    ...additionalParams,
  });
}

/**
 * Track begin checkout
 */
export function trackBeginCheckout(
  items: GA4Item[],
  currency: string,
  value: number,
  additionalParams: Record<string, unknown> = {},
): void {
  trackEcommerceEvent(GTMEvents.BEGIN_CHECKOUT, {
    currency,
    value,
    items,
    ...additionalParams,
  });
}

/**
 * Track purchase
 */
export function trackPurchase(
  transactionId: string,
  items: GA4Item[],
  currency: string,
  value: number,
  tax?: number,
  shipping?: number,
  coupon?: string,
  additionalParams: Record<string, unknown> = {},
): void {
  trackEcommerceEvent(GTMEvents.PURCHASE, {
    transaction_id: transactionId,
    currency,
    value,
    items,
    tax,
    shipping,
    coupon,
    ...additionalParams,
  });
}

/**
 * Track search
 */
export function trackSearch(
  searchTerm: string,
  additionalParams: Record<string, unknown> = {},
): void {
  trackGTMEvent(GTMEvents.SEARCH, {
    search_term: searchTerm,
    ...additionalParams,
  });
}

/**
 * Track sign up
 */
export function trackSignUp(method: string, additionalParams: Record<string, unknown> = {}): void {
  trackGTMEvent(GTMEvents.SIGN_UP, {
    method,
    ...additionalParams,
  });
}

/**
 * Helper to convert TUTULY product to GA4 item format
 */
export function convertProductToGA4Item(
  product: {
    id: string;
    name: string;
    price: number;
    currency: string;
    category?: string;
    variant?: string;
    brand?: string;
  },
  quantity = 1,
): GA4Item {
  return {
    item_id: product.id,
    item_name: product.name,
    item_brand: product.brand || "TUTULY",
    item_category: product.category,
    item_variant: product.variant,
    price: product.price,
    quantity,
    currency: product.currency,
  };
}

/**
 * Safe tracking wrapper that only runs on client
 */
export function useGTMTracking() {
  return {
    trackEvent: trackGTMEvent,
    trackEcommerceEvent,
    trackPageView,
    trackProductView,
    trackAddToCart,
    trackBeginCheckout,
    trackPurchase,
    trackSearch,
    trackSignUp,
    convertProductToGA4Item,
    GTMEvents,
  };
}
